import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { z } from "zod";
import { Account } from "@common/types/domains/accounting/account";
import { ExtendedAccount } from "@common/types/reports/types";
import {
    AccountMergeBlocker,
    AccountMergeBlockerCode,
    accountMergeBlockerSchema,
    isLockedPeriodBlocker,
} from "@common/schemas/domains/accounting/account.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    useMergeAccountsPreview,
    useMergeAccountsMutation,
} from "@api/account.api";
import {
    PlainModal,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Alert } from "@design-system/Alert/Alert";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { VStack } from "@design-system/VStack/VStack";
import { ReconciliationRanges } from "@components/reconciliation/ReconciliationRanges/ReconciliationRanges";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { formatIsoDateForDisplay } from "@helpers/date";
import { MergeTargetAccountCombobox } from "./MergeTargetAccountCombobox";
import { MergeUnlockedPeriodsInsteadModal } from "./MergeUnlockedPeriodsInsteadModal";
import styles from "./MergeChartOfAccountsModal.module.scss";

interface Props {
    show?: boolean;
    onHide?: () => void;
    account: ExtendedAccount;
    onMerged?: () => void;
    onMutation?: (promise: Promise<unknown>) => void;
}

const mergeErrorBodySchema = z.object({
    message: z.string(),
    blockers: z.array(accountMergeBlockerSchema).optional(),
});

type MergeOutcome = "merged" | "refused" | "failed";

const BlockerItem: React.FC<{
    blocker: AccountMergeBlocker;
    targetAccountName: string;
}> = ({ blocker, targetAccountName }) => (
    <VStack gap={4}>
        <span>{blocker.message}</span>
        {blocker.code === AccountMergeBlockerCode.LOCKED_PERIOD && (
            <span>
                Books are locked on or before{" "}
                <strong>{formatIsoDateForDisplay(blocker.lockDate)}</strong>.
            </span>
        )}
        {blocker.code === AccountMergeBlockerCode.TARGET_RECONCILED && (
            <ReconciliationRanges
                title={targetAccountName}
                ranges={blocker.overlappingRanges}
            />
        )}
    </VStack>
);

export const MergeChartOfAccountsModal: React.FC<Props> = ({
    show,
    onHide,
    account,
    onMerged,
    onMutation,
}) => {
    const { error: toastError } = useToaster();
    const [targetAccount, setTargetAccount] = useState<Account>();
    const [executeBlockers, setExecuteBlockers] = useState<
        AccountMergeBlocker[]
    >([]);
    const [showMergeUnlockedModal, setShowMergeUnlockedModal] = useState(false);

    const excludeIds = useMemo(() => [account.id], [account.id]);

    const preview = useMergeAccountsPreview({
        entityId: account.entityId,
        sourceAccountId: account.id,
        targetAccountId: targetAccount?.id,
    });

    const mergeMutation = useMergeAccountsMutation();

    const onSelectTarget = useCallback((selected: Account | null) => {
        setTargetAccount(selected ?? undefined);
        setExecuteBlockers([]);
    }, []);

    const previewData = preview.data;

    const reportedBlockers = executeBlockers.length
        ? executeBlockers
        : (previewData?.blockers ?? []);
    const hasBlockers = reportedBlockers.length > 0;

    const lockedPeriodBlocker = reportedBlockers.find(isLockedPeriodBlocker);
    const hasRefusingBlocker = reportedBlockers.some(
        (blocker) => !isLockedPeriodBlocker(blocker),
    );
    const isLockedPeriodOnly =
        lockedPeriodBlocker !== undefined && !hasRefusingBlocker;

    useEffect(() => {
        if (lockedPeriodBlocker === undefined) {
            setShowMergeUnlockedModal(false);
        }
    }, [lockedPeriodBlocker]);

    const canMerge =
        !!targetAccount &&
        !!previewData &&
        !preview.isLoading &&
        (!hasBlockers || isLockedPeriodOnly);

    const performMerge = useCallback(
        async ({
            mergeUnlockedPeriodsOnly,
        }: {
            mergeUnlockedPeriodsOnly?: boolean;
        }): Promise<MergeOutcome> => {
            if (!targetAccount) {
                return "failed";
            }
            setExecuteBlockers([]);
            // Track before await so a drawer close mid-request still sees the
            // write.
            const mutation = mergeMutation.mutateAsync({
                entityId: account.entityId,
                body: {
                    sourceAccountId: account.id,
                    targetAccountId: targetAccount.id,
                    mergeUnlockedPeriodsOnly,
                },
            });
            onMutation?.(mutation);
            try {
                await mutation;
            } catch (error) {
                const parsed = isAxiosError(error)
                    ? mergeErrorBodySchema.safeParse(error.response?.data)
                    : undefined;
                if (parsed?.success) {
                    setExecuteBlockers(parsed.data.blockers ?? []);
                    toastError(parsed.data.message);
                    return "refused";
                }
                toastError("Failed to merge accounts");
                return "failed";
            }
            onHide?.();
            onMerged?.();
            return "merged";
        },
        [
            targetAccount,
            mergeMutation,
            account.entityId,
            account.id,
            toastError,
            onHide,
            onMerged,
            onMutation,
        ],
    );

    const onSubmit = useCallback(async () => {
        // The preview already knows the period is locked, so open the
        // confirmation directly instead of firing a request known to be refused.
        if (isLockedPeriodOnly) {
            setShowMergeUnlockedModal(true);
            return;
        }
        await performMerge({});
    }, [isLockedPeriodOnly, performMerge]);

    // A refusal is shown in the parent alert behind the confirmation, so only
    // a generic failure, where retrying makes sense, keeps it open.
    const onConfirmMergeUnlocked = useCallback(async () => {
        const outcome = await performMerge({ mergeUnlockedPeriodsOnly: true });
        if (outcome !== "failed") {
            setShowMergeUnlockedModal(false);
        }
    }, [performMerge]);

    const targetAccountName = targetAccount?.name ?? "";

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>Merge {account.name}</StandardModalHeader>
            <VStack gap={16}>
                <p className="mb-0">
                    Select the account to merge <strong>{account.name}</strong>{" "}
                    into. Its transactions, journal entries and history move to
                    the chosen account, then <strong>{account.name}</strong> is{" "}
                    {isLockedPeriodOnly ? "archived" : "removed"}.
                </p>

                <MergeTargetAccountCombobox
                    entityId={account.entityId}
                    accountClass={account.class}
                    selectedAccount={targetAccount}
                    onChange={onSelectTarget}
                    excludeIds={excludeIds}
                />

                {targetAccount && previewData && !hasBlockers && (
                    <p className="mb-0">
                        <strong>{account.name}</strong> will be removed.
                        {previewData.impact.journalEntryLineCount > 0 && (
                            <>
                                {" "}
                                {pluralizeWithCount(
                                    "journal entry line",
                                    previewData.impact.journalEntryLineCount,
                                )}{" "}
                                will be reassigned to{" "}
                                <strong>{targetAccountName}</strong>.
                            </>
                        )}
                        {previewData.openingBalance.source.length > 0 && (
                            <>
                                {" "}
                                The opening balance and any adjustment will move
                                to <strong>{targetAccountName}</strong>.
                            </>
                        )}
                    </p>
                )}

                {targetAccount && hasBlockers && (
                    <Alert
                        variant={isLockedPeriodOnly ? "warning" : "danger"}
                        className={styles.blockers}
                    >
                        <VStack gap={8}>
                            {reportedBlockers.map((blocker) => (
                                <BlockerItem
                                    key={blocker.code}
                                    blocker={blocker}
                                    targetAccountName={targetAccountName}
                                />
                            ))}
                        </VStack>
                    </Alert>
                )}

                {targetAccount && preview.isError && !hasBlockers && (
                    <Alert variant="danger" className={styles.blockers}>
                        <VStack gap={8}>
                            <span>
                                We couldn&apos;t check whether this merge is
                                possible.
                            </span>
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                onClick={() => preview.refetch()}
                            >
                                Try again
                            </Button>
                        </VStack>
                    </Alert>
                )}

                <StandardModalFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onHide}
                        size="xl"
                        disabled={mergeMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        type="button"
                        variant="primary"
                        size="xl"
                        loading={mergeMutation.isPending}
                        disabled={!canMerge || mergeMutation.isPending}
                        onClick={onSubmit}
                    >
                        Merge
                    </ButtonWithLoader>
                </StandardModalFooter>
            </VStack>
            {lockedPeriodBlocker && (
                <MergeUnlockedPeriodsInsteadModal
                    show={showMergeUnlockedModal}
                    sourceAccountName={account.name}
                    targetAccountName={targetAccountName}
                    lockDate={lockedPeriodBlocker.lockDate}
                    onConfirm={onConfirmMergeUnlocked}
                    onHide={() => setShowMergeUnlockedModal(false)}
                />
            )}
        </PlainModal>
    );
};
